import { useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { Check, ImageOff, Images, Loader2, SkipForward, X } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { KbMediaLibrary } from "@/components/KbMediaLibrary";
import { kbImageName } from "@/lib/kbPasteImages";

/** The replace-this-picture controls, floated against the picture itself so the
 *  editor can see what they are swapping out. Pick from the library, paste a
 *  screenshot or an address, then save and move to the next broken one. */
export function KbImageFixBar({
  fixUrl, replaced, remaining, saving, anchor, imageNumber, pastImages = [],
  onReplace, onSaveNext, onSkip, onDone,
}) {
  const [library, setLibrary] = useState(false);
  const [url, setUrl] = useState("");
  const [working, setWorking] = useState(false);

  const applyUrl = async (raw) => {
    const v = (raw || "").trim();
    if (!v) return;
    setWorking(true);
    try {
      if (v.startsWith("/api/kb/assets/")) onReplace(v);
      else {
        // Re-host it now, so we never depend on someone else's CDN again.
        const { data } = await api.post("/kb/upload-from-url", { url: v });
        onReplace(data.url);
      }
      setUrl("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't use that address");
    } finally { setWorking(false); }
  };

  // A screenshot on the clipboard is the fastest replacement there is.
  const paste = async (e) => {
    const file = [...(e.clipboardData?.files || [])][0];
    if (!file) return;                       // a pasted URL falls through to the box
    e.preventDefault();
    setWorking(true);
    const fd = new FormData();
    fd.append("file", file, imageNumber
      ? kbImageName(imageNumber, file.type) : (file.name || "pasted.png"));
    try {
      const { data } = await api.post("/kb/upload", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      onReplace(data.url);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't upload that");
    } finally { setWorking(false); }
  };

  const busy = working || saving;
  const style = anchor
    ? { top: Math.min(anchor.top + 8, window.innerHeight - 130),
        left: Math.max(12, Math.min(anchor.left, window.innerWidth - 480)) }
    : { bottom: 20, left: "50%", transform: "translateX(-50%)" };

  const panel = (
    <div data-testid="kb-image-fix-bar" style={{ position: "fixed", zIndex: 60, ...style }}
      className={`w-[460px] max-w-[calc(100vw-24px)] rounded-xl border p-2.5 shadow-xl backdrop-blur-sm ${replaced
        ? "border-emerald-300 bg-emerald-50/95 dark:border-emerald-800 dark:bg-emerald-950/90"
        : "border-primary/40 bg-white/95 dark:border-primary/40 dark:bg-slate-900/95"}`}>
      <div className="flex items-start gap-2">
        {replaced
          ? <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
          : <ImageOff className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />}
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold text-slate-800 dark:text-slate-100">
            {replaced ? "Replacement in place — save to clear the alert"
              : "This picture is broken — put a new one in its place"}
          </div>
          <div className="truncate text-[10px] text-slate-500 dark:text-slate-400"
            data-testid="kb-image-fix-url" title={fixUrl}>
            {replaced ? "The dead picture has been swapped out." : fixUrl}
          </div>
        </div>
        <button type="button" onClick={onDone} data-testid="kb-image-fix-done" disabled={busy}
          className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800"
          title="Done for now — back to Image & link health">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {!replaced && (
        <div className="mt-2 flex items-center gap-1.5">
          <Button size="sm" variant="outline" data-testid="kb-image-fix-library"
            disabled={busy} onClick={() => setLibrary(true)} className="h-7 shrink-0 gap-1.5 text-xs">
            {working ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Images className="h-3.5 w-3.5" />}
            Pick from library
          </Button>
          <Input data-testid="kb-image-fix-url-input" value={url} disabled={busy}
            onChange={(e) => setUrl(e.target.value)} onPaste={paste}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyUrl(url); } }}
            placeholder="…or paste an image / address"
            className="h-7 bg-white text-xs dark:bg-slate-900" />
          {!!url.trim() && (
            <Button size="sm" data-testid="kb-image-fix-use-url" disabled={busy}
              onClick={() => applyUrl(url)} className="h-7 shrink-0 text-xs">Use this</Button>
          )}
        </div>
      )}

      {!replaced && !!pastImages.length && (
        <div className="mt-2" data-testid="kb-image-fix-past">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
            This article used to show
          </div>
          <div className="mt-1 flex gap-1.5 overflow-x-auto pb-1">
            {pastImages.map((p) => (
              <button key={p.asset_id} type="button" title={`${p.filename} — ${p.why}`}
                data-testid={`kb-image-fix-past-${p.asset_id}`}
                onClick={() => onReplace(p.url)} disabled={busy}
                className="shrink-0 overflow-hidden rounded border border-slate-200 transition-colors hover:border-primary dark:border-slate-700">
                <img src={p.url} alt={p.filename} className="h-12 w-16 object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mt-2 flex items-center gap-1.5">
        <Button size="sm" data-testid="kb-image-fix-save-next" disabled={busy || !replaced}
          onClick={onSaveNext} className="h-7 gap-1.5 text-xs">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <SkipForward className="h-3.5 w-3.5" />}
          Save &amp; next
        </Button>
        <Button size="sm" variant="ghost" data-testid="kb-image-fix-skip" disabled={busy}
          onClick={onSkip} className="h-7 text-xs">Skip this one</Button>
        {remaining > 0 && (
          <span data-testid="kb-image-fix-remaining"
            className="ml-auto rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-500 dark:bg-slate-800 dark:text-slate-400">
            {remaining} left
          </span>
        )}
      </div>

      <KbMediaLibrary open={library} onOpenChange={setLibrary} kind="image"
        onPick={(asset) => onReplace(asset.url)} />
    </div>
  );

  return createPortal(panel, document.body);
}
